import React, { useEffect, useState, useRef } from 'react';
import { User, Calendar, ChevronDown, Check } from 'lucide-react';
import { MortgageDepotLogo } from './MortgageDepotLogo';
const LOAN_OFFICERS = ['John Davis', 'Sarah Mitchell', 'Michael Chen', 'Jessica Rodriguez', 'David Thompson', 'Emily Parker'];
const MONTHS = ['January 2025', 'February 2025', 'March 2025', 'April 2025', 'May 2025', 'June 2025', 'July 2025', 'August 2025', 'September 2025', 'October 2025', 'November 2025', 'December 2025'];
interface DropdownProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  icon: React.ReactNode;
  iconRounded?: boolean;
}
function Dropdown({
  label,
  value,
  options,
  onChange,
  icon,
  iconRounded
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);
  return <div className="relative" ref={ref} data-id="element-341" data-mp="3fdf197hspnr3">
      <button type="button" onClick={() => setOpen(!open)} aria-haspopup="listbox" aria-expanded={open} aria-label={`${label}: ${value}`} className="flex items-center gap-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-4 py-2 transition-colors min-w-[180px] focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-342" data-mp="3abpqlsyhjc3j">
        <div className={`bg-[#0a1e5e] text-white p-1.5 ${iconRounded ? 'rounded-full' : 'rounded-md'}`} data-id="element-343" data-mp="3tbdda68n983f">
          {icon}
        </div>
        <div className="text-left flex-1" data-id="element-344" data-mp="1recyxqsr7dp9">
          <p className="text-xs text-gray-500 font-semibold uppercase" data-id="element-345" data-mp="1wceg266md2y3">
            {label}
          </p>
          <p className="text-sm font-bold text-[#0a1e5e]" data-id="element-346" data-mp="1ho9vewo0ahco">{value}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-347" data-mp="1mmuvmw39d114" />
      </button>

      {open && <ul role="listbox" className="absolute right-0 top-full mt-2 w-full min-w-[220px] bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto py-1" data-id="element-348" data-mp="18ls8eg0j1kom">
          {options.map(option => {
        const selected = option === value;
        return <li key={option} role="option" aria-selected={selected} tabIndex={0} onClick={() => {
          onChange(option);
          setOpen(false);
        }} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onChange(option);
            setOpen(false);
          }
        }} className={`flex items-center justify-between px-4 py-2 text-sm cursor-pointer hover:bg-blue-50 ${selected ? 'bg-blue-50 text-[#0a1e5e] font-bold' : 'text-gray-700 font-medium'}`} data-id="element-349" data-mp="13ptqnb9pf0x6">
                <span data-id="element-350" data-mp="106bcbxxrc4ig">{option}</span>
                {selected && <Check className="w-4 h-4 text-blue-600" data-id="element-351" data-mp="105jlf0f3uuyi" />}
              </li>;
      })}
        </ul>}
    </div>;
}
interface HeaderProps {
  loanOfficer?: string;
  month?: string;
  onLoanOfficerChange?: (value: string) => void;
  onMonthChange?: (value: string) => void;
}
export function Header({
  loanOfficer: controlledLO,
  month: controlledMonth,
  onLoanOfficerChange,
  onMonthChange
}: HeaderProps = {}) {
  const [internalLO, setInternalLO] = useState('John Davis');
  const [internalMonth, setInternalMonth] = useState('May 2025');
  const loanOfficer = controlledLO ?? internalLO;
  const month = controlledMonth ?? internalMonth;
  const handleLO = (val: string) => {
    setInternalLO(val);
    onLoanOfficerChange?.(val);
  };
  const handleMonth = (val: string) => {
    setInternalMonth(val);
    onMonthChange?.(val);
  };
  return <header className="bg-white border-b border-gray-200 px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-352" data-mp="1k8yvi7cls55s">
      <div className="flex items-center gap-6" data-id="element-353" data-mp="1p7s9s1zps070">
        <MortgageDepotLogo className="h-14 w-auto" data-id="element-354" data-mp="1t944glxg3bm2" />
        <div className="hidden md:block h-10 w-px bg-gray-300" data-id="element-355" data-mp="1t7ay77qqutel"></div>
        <div data-id="element-356" data-mp="1xzu6pqakqycj">
          <h2 className="text-3xl font-extrabold text-[#0a1e5e] tracking-tight" data-id="element-357" data-mp="1y0dsxj79c8wd">
            SCORECARD
          </h2>
          <p className="text-sm font-semibold text-blue-600 tracking-widest" data-id="element-358" data-mp="11hfuu761x36x">
            FOCUS. EXECUTE. CLOSE.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3" data-id="element-359" data-mp="y0a87ypnzvig">
        <Dropdown label="Loan Officer" value={loanOfficer} options={LOAN_OFFICERS} onChange={handleLO} icon={<User className="w-4 h-4" data-id="element-361" data-mp="oykh6tzp2yl7" />} iconRounded data-id="element-360" data-mp="xyz5m27ygtsx" />
        <Dropdown label="Month" value={month} options={MONTHS} onChange={handleMonth} icon={<Calendar className="w-4 h-4" data-id="element-363" data-mp="f163h255onwl" />} data-id="element-362" data-mp="p0wzx9da1czb" />
      </div>
    </header>;
}